import SyncIcon from '@mui/icons-material/Sync';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';

import React, { useEffect, useMemo, useState } from 'react';

import { ServiceTitanZone } from '_lib/api/apiTypes.shim';
import {
  DataGrid,
  GridColDef,
  gridPageSizeSelector,
  GridRowSelectionModel,
  useGridApiRef
} from '_lib/components/DataGrid/DataGrid';
import { FlexBox } from '_lib/components/FlexBox/FlexBox';
import { Tooltip } from '_lib/components/Tooltip/Tooltip';

/**
 * Processed business unit display information
 */
export type BusinessUnitItem = {
  names: string[];
  display: string;
  isMultiple?: boolean;
};

/**
 * Processed zip code item with zone and business unit metadata
 */
export type ZipCodeItem = {
  zip: string;
  zone: ServiceTitanZone | null; // null means Relate-only (not synced with Service Titan)
  businessUnitsItem: BusinessUnitItem | null;
  selected: boolean;
};

export type SyncStatus = 'ALL' | 'Not Synced' | 'Synced';

export type ZipCodeTableProps = {
  zipCodeItems: ZipCodeItem[];
  setSelectedZipCodes?: (selectedZipCodes: string[]) => void;
  showSyncStatusColumn?: boolean;
  readonlyMode?: boolean;
};

export const ZipCodeTable = (props: ZipCodeTableProps) => {
  const { zipCodeItems, setSelectedZipCodes, showSyncStatusColumn, readonlyMode } = props;

  const apiRef = useGridApiRef();

  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set()
  });

  useEffect(() => {
    setRowSelectionModel({
      type: 'include',
      ids: new Set(zipCodeItems.filter((item) => item.selected).map((item) => item.zip))
    });
  }, [zipCodeItems]);

  const columns = useMemo(() => {
    const baseColumns: GridColDef<ZipCodeItem>[] = [
      { field: 'zip', headerName: 'Zip Code', flex: 1 },
      {
        field: 'zoneName',
        headerName: 'Zone Name',
        flex: 1,
        valueGetter: (_value, row) => (row.zone ? row.zone.name : '--')
      },
      {
        field: 'businessUnit',
        headerName: 'Business Unit(s)',
        flex: 1,
        valueGetter: (_value, row) => (row.businessUnitsItem ? row.businessUnitsItem.display : '--'),
        renderCell(params) {
          if (params.row.businessUnitsItem?.isMultiple) {
            return (
              <Tooltip title={params.row.businessUnitsItem.display}>
                <span>Multiple Business Units</span>
              </Tooltip>
            );
          }

          return params.row.businessUnitsItem ? params.row.businessUnitsItem.display : '--';
        }
      }
    ];
    if (showSyncStatusColumn) {
      baseColumns.push({
        field: 'syncStatus',
        headerName: 'Status',
        flex: 1,
        valueGetter: (_value, row) => (row.zone ? 'Yes' : 'No'),
        renderCell(params) {
          return params.row.zone ? (
            <Tooltip title='Synced'>
              <SyncIcon color='success' />
            </Tooltip>
          ) : (
            <Tooltip title='Not Synced'>
              <SyncProblemIcon color='error' />
            </Tooltip>
          );
        }
      } satisfies GridColDef<ZipCodeItem>);
    }

    return baseColumns;
  }, [showSyncStatusColumn]);

  return (
    <FlexBox sx={{ maxHeight: '750px', overflow: 'scroll', width: '100%' }}>
      <DataGrid
        checkboxSelection={!readonlyMode}
        showToolbar
        onRowSelectionModelChange={(newRowSelectionModel) => {
          if (newRowSelectionModel.type === 'exclude' && newRowSelectionModel.ids.size === 0) {
            const allSelected = zipCodeItems.map((item) => item.zip);
            setRowSelectionModel({ type: 'include', ids: new Set(allSelected) });
            setSelectedZipCodes?.(allSelected);
            return;
          }

          setRowSelectionModel(newRowSelectionModel);
          setSelectedZipCodes?.(Array.from(newRowSelectionModel.ids).map((id) => id.toString()));
        }}
        onRowCountChange={(count) => {
          const pageSize = gridPageSizeSelector(apiRef);
          const pagesNumber = Math.ceil(count / pageSize);
          apiRef.current?.setPage(pagesNumber);

          // 🤷‍♂️ apparently we need to wait a render cycle for this to work?
          setTimeout(() => {
            apiRef.current?.scrollToIndexes({ rowIndex: count - 1 });
          }, 0);
        }}
        rowSelectionModel={rowSelectionModel}
        columns={columns}
        rows={zipCodeItems}
        disableRowSelectionOnClick
        getRowId={(row) => row.zip}
        apiRef={apiRef}
      />
    </FlexBox>
  );
};
